import { createClient } from "@supabase/supabase-js";
import React, { useEffect, useState } from "react";
import { useGeolocation } from "src/hooks/useGeolocation";
import { Tables } from "../../supabase/supabase";
import Snowfall from "../holidays/Snowfall";
import PhotoLoader from "./PhotoLoader";
import TriviaFinish from "./TriviaFinish";
import { TriviaQuestion, createTriviaQuestions } from "./TriviaQuestion";
import TriviaRound from "./TriviaRound";
import TriviaStart from "./TriviaStart";

const supabaseUrl = process.env.REACT_APP_SUPABASE_URL || "url";
const supabaseKey = process.env.REACT_APP_SUPABASE_KEY || "xyz";
const supabase = createClient(supabaseUrl, supabaseKey);

const NUMBER_OF_QUESTIONS = 10;
const NUMBER_OF_TRIVIA_SCORES_TO_SHOW = 10;

interface TriviaGameProps {
  year: number;
  questionBank: any[];
  questionRanges: any[];
}

export default function TriviaGame(props: TriviaGameProps) {
  const [name, setName] = useState<string>();
  const [gameRound, setGameRound] = useState(-1);
  const [questions, setQuestions] = useState<TriviaQuestion[]>([]);
  const [guess, setGuess] = useState<string>();
  const [score, setScore] = useState(0);
  const [isGameOver, setIsGameOver] = useState(false);
  const [todayTriviaScores, setTodayTriviaScores] = useState<
    Tables<"triviaScores">[]
  >([]);
  const [allTimeTriviaScores, setAllTimeTriviaScores] = useState<
    Tables<"triviaScores">[]
  >([]);
// fix later: change var name
  const [allTimeTriviaScoresForGlobe, setAllTimeTriviaScoresForGlobe] = useState<
    Tables<"triviaScores">[]
  >([]);
  const [userId, setUserId] = useState<number>(0);
  const [isSaving, setIsSaving] = useState(false);
  const [locationData] = useGeolocation();

  useEffect(() => {
    setUserId(Math.floor(Math.random() * 1000000)); // Doesn't have to be secure; just used for matching scoreboard entries with current user.
  }, []);

  useEffect(() => {
    if (!isSaving) {
      // Only update the scores once the saving is complete.
      const updateTodayTriviaScores = async (numberOfScoresToShow: number) => {
        const today = new Date();
        const startOfDay = new Date(
          today.getFullYear(),
          today.getMonth(),
          today.getDate()
        );
        const endOfDay = new Date(
          today.getFullYear(),
          today.getMonth(),
          today.getDate(),
          23,
          59,
          59
        );

        const { data } = await supabase
          .from("triviaScores")
          .select("*")
          .eq("year", props.year)
          .gte("created_at", startOfDay.toISOString())
          .lte("created_at", endOfDay.toISOString())
          .order("score", { ascending: false })
          .order("created_at", { ascending: false })
          .limit(numberOfScoresToShow);

        setTodayTriviaScores(data ?? []);
      };

      const updateAllTimeTriviaScores = async (
        numberOfScoresToShow: number
      ) => {
        const { data } = await supabase
          .from("triviaScores")
          .select("*")
          .eq("year", props.year)
          .order("score", { ascending: false })
          .order("created_at", { ascending: false })
          .limit(numberOfScoresToShow);

        setAllTimeTriviaScores(data ?? []);
      };

      const updateAllTimeTriviaScoresForGlobe = async () => {
        const { data } = await supabase
          .from("triviaScores")
          .select("*")
          .eq("year", props.year)
          .order("score", { ascending: false })
          .order("created_at", { ascending: false });

        setAllTimeTriviaScoresForGlobe(data ?? []);
      };

      updateTodayTriviaScores(NUMBER_OF_TRIVIA_SCORES_TO_SHOW).catch(
        console.error
      );
      updateAllTimeTriviaScores(NUMBER_OF_TRIVIA_SCORES_TO_SHOW).catch(
        console.error
      );
      updateAllTimeTriviaScoresForGlobe().catch(console.error);
    }
  }, [isSaving, props.year]);

  window.onbeforeunload = function () {
    if (gameRound >= 0) {
      return "Data will be lost if you leave the page, are you sure?";
    }
  };

  function updateName(name: string) {
    setName(name.trim());
  }

  function beginQuiz() {
    setQuestions(
      createTriviaQuestions(
        props.questionBank,
        props.questionRanges,
        NUMBER_OF_QUESTIONS
      )
    );
    setGameRound(0);
  }

  function goToNextQuestion() {
    setGuess(undefined);
    setGameRound(gameRound + 1);
  }

  async function insertScore() {
    setIsSaving(true);
    const { error } = await supabase.from("triviaScores").insert({
      user_id: userId,
      name,
      score,
      year: props.year,
      latitude: locationData?.latitude || null,
      longitude: locationData?.longitude || null,
    });
    if (error) {
      console.error(error.message);
    }
    setIsSaving(false);
  }

  async function insertGuess(question: TriviaQuestion, guess: string) {
    setIsSaving(true);
    const { error } = await supabase.from("triviaAnswers").insert({
      name,
      question_id: question.id,
      guess,
      is_correct: question.isCorrect(guess),
      year: props.year,
    });
    if (error) {
      console.error(error.message);
    }
    setIsSaving(false);
  }

  async function finishGame() {
    await insertScore();
    setGuess(undefined);
    setIsGameOver(true);
  }

  function playAgain() {
    setIsGameOver(false);
    setScore(0);
    beginQuiz();
  }

  async function makeGuessWithQuestion(
    guess: string,
    question: TriviaQuestion
  ) {
    await insertGuess(question, guess);
    setGuess(guess);
    if (question.isCorrect(guess)) {
      setScore(score + 1);
    }
  }

  return (
    <div>
      <Snowfall />
      <PhotoLoader questionBank={props.questionBank} />
      {isGameOver ? (
        <TriviaFinish
          questions={questions}
          playAgain={playAgain}
          score={score}
          todayTriviaScores={todayTriviaScores}
          allTimeTriviaScores={allTimeTriviaScores}
          userId={userId}
        />
      ) : gameRound < 0 ? (
        <TriviaStart
          beginQuiz={beginQuiz}
          updateName={updateName}
          todayTriviaScores={todayTriviaScores}
          allTimeTriviaScores={allTimeTriviaScores}
          allTimeTriviaScoresForGlobe={allTimeTriviaScoresForGlobe}
          name={name}
          userId={userId}
          year={props.year}
        />
      ) : (
        <TriviaRound
          question={questions[gameRound]}
          numberOfQuestions={questions.length}
          roundNumber={gameRound}
          score={score}
          makeGuessWithQuestion={makeGuessWithQuestion}
          goToNextQuestion={goToNextQuestion}
          finishGame={finishGame}
          guess={guess}
          isSaving={isSaving}
          year={props.year}
        />
      )}
    </div>
  );
}
